'use client';

import { useTranslation } from 'react-i18next';
import { useI18n } from './client-translation';
import { getLocale } from './locale';
import { type Language } from './i18n-config';
import { type TFunction, TOptions } from 'i18next';
import { getAppConfig } from '@bts-web/core-app-config';
import { useEffect } from 'react';
import { useServerLanguage } from './LanguageContext/useServerLanguage';

export const useClientLocale = () => {
  const i18n = useI18n();

  const { serverLanguage } = useServerLanguage();

  // Determine the language to use
  const language = (serverLanguage ||
    i18n.resolvedLanguage ||
    i18n.language) as Language;

  return getLocale(language);
};

const getCallerInfo = (): string[] => {
  const callerFileNames: string[] = [];

  const stack = new Error().stack;

  if (!stack) return ['unknown'];

  const lines = stack.split('\n');

  for (let i = 2; i < lines.length; i++) {
    const line = lines[i];

    if (
      line.includes('.tsx') ||
      line.includes('.ts') ||
      line.includes('.jsx') ||
      line.includes('.js')
    ) {
      const match = line.match(/\/([^/]+\.(?:tsx?|jsx?)):/);

      if (match) {
        callerFileNames.push(match[1]);
      }
    }
  }

  if (callerFileNames.length > 0) {
    return callerFileNames;
  }

  callerFileNames.push('unknown');

  return callerFileNames;
};
// Main client translation hook, aliased from react-i18next.

/**
 * Should be use with care as it might cause `Hydration Error`. Before using it consider:
 *  - Can translations be passed as props even if it's more then 1 level?
 *  - Can `interpolateTranslation` be used for parametrized translations?
 *
 * Use `useServerLanguage` hook to pass in language.
 */
export const useClientTranslation = () => {
  /**
   * keys in lokalise:
   *
   * "welcome_test_@tenant": "Welcome to the app",
   * "welcome_test_@tenant_lbbw": "Welcome to lbbw",
   * "welcome_test_@tenant_raiffeisen": "Welcome to raiffeisen",
   * "welcome_test_@tenant_rakbank": "Welcome to rakbank",
   *
   * Suffix comes from AppConfig.tenantTranslationKeySuffix.
   *
   *
   * usage:
   *
   * t(`welcome_test_@tenant`, {
   *   defaultValue: 'Translation not found',
   * })
   */

  // Get the custom i18n instance
  const i18n = useI18n();

  // Use react-i18next's useTranslation with our custom instance
  const { t: reactI18nextT } = useTranslation('general', { i18n });

  const serverLanguage = useServerLanguage().serverLanguage;

  const locale = getLocale(serverLanguage as Language);

  const currentClientLanguage = (i18n.resolvedLanguage ||
    i18n.language) as Language;

  // Sync client language with server language when it changes
  useEffect(() => {
    if (currentClientLanguage !== serverLanguage && serverLanguage) {
      i18n.changeLanguage(serverLanguage);
    }
  }, [serverLanguage, currentClientLanguage, i18n]);

  return {
    ...i18n, // Spread all original i18n properties
    t: ((baseKey: string, options: TOptions = {}) => {
      if (!baseKey) {
        console.warn(
          `useTranslation called from: ${getCallerInfo().join(' <- ')}, with no translation key`,
        );

        return '';
      }

      const keys = baseKey.includes('_@tenant')
        ? [`${baseKey}_${getAppConfig().tenantTranslationKeySuffix}`, baseKey]
        : baseKey;

      const result = String(reactI18nextT(keys, options));

      return result === '' ? `${baseKey} (empty)` : result;
    }) as TFunction,
    locale,
  };
};
